import React, { useState, useEffect, useMemo } from 'react';
import {
    Button,
    Typography,
    Box,
    LinearProgress,
    Tabs,
    Tab,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Badge,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DemoContainer, DemoItem } from '@mui/x-date-pickers/internals/demo';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Slide from '@mui/material/Slide';
import { hasPermission, getFormattedDate, getRoomLabelText } from '@utilities/common';
import { TransitionProps } from '@mui/material/transitions';
import moment from 'moment';
import dayjs from 'dayjs';
import { updateCollection, deleteCollection } from '@utilities/auth';
import {
    buildFoodOrderSnapshot,
    logOrderEventAsync,
} from '@utilities/orderLogging';
import { updateOrderBill } from '@utilities/order';
import { connect, useDispatch } from 'react-redux';
import { logError } from '@actions/userActions';
import EditOrderMenuPanel from './EditOrderMenuPanel';
import EditOrderCart from './EditOrderCart';
import {
    cloneOrderCart,
    sanitizeEditCart,
    computeBillFieldsFromCart,
} from './editOrderUtils';
import './UpdateOrderDialog.scss';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const UpdateOrder = ({
    data,
    onClose,
    roles,
    selectedRestId,
    userPermissions,
}: any) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { isOpen, orderId, createdAtDate, uoId, order: orderFromList } = data;
    const [open, setOpen] = useState(isOpen);
    const [oId, setOId] = useState(orderId);
    const [uId, setUoId] = useState(uoId);
    const [order, setOrder] = useState<any>(orderFromList || null);
    const [formData, setFormData] = useState(createdAtDate);
    const [editCart, setEditCart] = useState<Record<string, any>>({});
    const [isSavingBill, setIsSavingBill] = useState(false);
    const [mobilePanel, setMobilePanel] = useState(0);

    const handleClose = (isUpdated: boolean = false) => {
        setOpen(false);
        onClose(isUpdated);
    };

    const canUpdateOrder =
        (roles &&
            selectedRestId &&
            ['admin'].includes(roles[selectedRestId].role)) ||
        hasPermission(userPermissions, 'updateOrder', 'write');
    const canDeleteOrder =
        (roles &&
            selectedRestId &&
            ['admin'].includes(roles[selectedRestId].role)) ||
        hasPermission(userPermissions, 'updateOrder', 'delete');

    const handleDateChange = (event: any) => {
        setFormData(event?.toISOString());
    };

    const handleUpdateOrderDate = () => {
        if (!oId || !formData) return;
        const restaurantId =
            selectedRestId ||
            order?.restaurantId ||
            order?.restaurant?.docId ||
            '';
        const previous = buildFoodOrderSnapshot(order || { uoId, createdAt: order?.createdAt });
        const nextCreatedAt = moment(formData).utc().valueOf();
        const next = buildFoodOrderSnapshot({
            ...(order || {}),
            uoId,
            createdAt: nextCreatedAt,
        });
        const clientRequestId =
            typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : `food-upd-date-${Date.now()}`;

        updateCollection('orders', oId, 'createdAt', nextCreatedAt).then((ok) => {
            logOrderEventAsync({
                restaurantId: String(restaurantId || 'unknown'),
                orderId: oId,
                collectionName: 'orders',
                orderKind: 'food',
                action: 'update',
                status: ok ? 'success' : 'failed',
                source: 'admin-orders',
                errorMessage: ok ? null : 'Failed to update order bill date',
                previous,
                next,
                snapshot: next,
                clientRequestId,
            });
            if (ok) {
                handleClose(true);
            } else {
                dispatch(
                    logError({
                        severity: 'error',
                        message: 'Failed to update order date',
                    })
                );
            }
        });
    };

    const handleDeleteOrder = () => {
        if (!oId) return;
        const restaurantId =
            selectedRestId ||
            order?.restaurantId ||
            order?.restaurant?.docId ||
            '';
        const snapshot = buildFoodOrderSnapshot(order || { uoId: uoId, orderId: oId });
        const clientRequestId =
            typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : `food-del-${Date.now()}`;

        deleteCollection('orders', oId).then((ok) => {
            logOrderEventAsync({
                restaurantId: String(restaurantId || 'unknown'),
                orderId: oId,
                collectionName: 'orders',
                orderKind: 'food',
                action: 'delete',
                status: ok ? 'success' : 'failed',
                source: 'admin-orders',
                errorMessage: ok ? null : 'Firestore delete failed or order id missing',
                snapshot,
                clientRequestId,
            });
            if (ok) {
                handleClose(true);
            } else {
                dispatch(
                    logError({
                        severity: 'error',
                        message: 'Failed to delete order',
                    })
                );
            }
        });
    };

    const handleSaveBill = async () => {
        if (!oId || !order) return;
        const sanitized = sanitizeEditCart(editCart);
        if (!Object.keys(sanitized).length) {
            dispatch(
                logError({
                    severity: 'error',
                    message: 'Add at least one item to the bill before saving.',
                })
            );
            return;
        }
        setIsSavingBill(true);
        const billFields = computeBillFieldsFromCart(sanitized, order);
        const previous = buildFoodOrderSnapshot(order);
        const next = buildFoodOrderSnapshot({ ...order, ...billFields });
        const restaurantId =
            selectedRestId ||
            order?.restaurantId ||
            order?.restaurant?.docId ||
            '';
        const clientRequestId =
            typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : `food-upd-bill-${Date.now()}`;

        const ok = await updateOrderBill(oId, billFields, dispatch);
        logOrderEventAsync({
            restaurantId: String(restaurantId || 'unknown'),
            orderId: oId,
            collectionName: 'orders',
            orderKind: 'food',
            action: 'update',
            status: ok ? 'success' : 'failed',
            source: 'admin-orders',
            errorMessage: ok ? null : 'Failed to update food order bill',
            previous,
            next,
            snapshot: next,
            clientRequestId,
        });
        setIsSavingBill(false);
        if (ok) {
            handleClose(true);
        }
    };

    useEffect(() => {
        const {
            isOpen: nextOpen,
            orderId: nextOrderId,
            createdAtDate: nextCreatedAt,
            uoId: nextUoId,
            order: nextOrder,
        } = data;
        setOpen(nextOpen);
        setFormData(nextCreatedAt);
        setOId(nextOrderId);
        setUoId(nextUoId);
        setOrder(nextOrder || null);
        if (nextOpen && nextOrder?.cart) {
            setEditCart(cloneOrderCart(nextOrder.cart));
            setMobilePanel(0);
        } else if (!nextOpen) {
            setEditCart({});
        }
    }, [data]);

    const billPreview = useMemo(
        () => (order ? computeBillFieldsFromCart(sanitizeEditCart(editCart), order) : null),
        [editCart, order]
    );

    const itemCount = useMemo(
        () =>
            Object.values(editCart).reduce(
                (sum: number, val: any) => sum + (val?.selected || 0),
                0
            ),
        [editCart]
    );

    const createdAtDisplay = order?.createdAt
        ? getFormattedDate(order.createdAt)
        : formData
          ? getFormattedDate(formData)
          : '';

    const tableLabel = order?.tableNumber
        ? getRoomLabelText(
              order.tableNumber?.split('-')[0],
              parseInt(order.tableNumber?.split('-')[1], 10)
          )
        : '';

    const metaChips = [
        { label: 'Order#', value: order?.orderSeqNo || uId },
        { label: 'Bill date', value: createdAtDisplay },
        tableLabel && { label: 'Table', value: tableLabel },
        order?.customerName && { label: 'Customer', value: order.customerName },
        order?.customerPhone && { label: 'Phone', value: order.customerPhone },
    ].filter(Boolean) as { label: string; value: string }[];

    return (
        <Dialog
            fullScreen
            open={open}
            onClose={() => handleClose()}
            TransitionComponent={Transition}
            className="edit-order-dialog"
        >
            <AppBar className="edit-order-dialog__appbar" sx={{ position: 'relative' }}>
                <Toolbar className="edit-order-dialog__toolbar">
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={() => handleClose()}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography
                        className="edit-order-dialog__title"
                        sx={{ ml: 1, flex: 1 }}
                        variant="h6"
                        component="div"
                    >
                        Edit Order Bill
                    </Typography>
                    <Box className="edit-order-dialog__toolbar-actions">
                        {canUpdateOrder && !isMobile && (
                            <Button
                                className="edit-order-dialog__save-btn"
                                variant="contained"
                                color="secondary"
                                disabled={isSavingBill}
                                onClick={handleSaveBill}
                                startIcon={<SaveIcon />}
                            >
                                Save Bill
                            </Button>
                        )}
                        {!isMobile && (
                            <Button
                                variant="outlined"
                                color="inherit"
                                onClick={() => handleClose()}
                                sx={{ borderColor: 'rgba(255,255,255,0.5)' }}
                            >
                                Cancel
                            </Button>
                        )}
                    </Box>
                </Toolbar>
            </AppBar>

            <Box className="edit-order-dialog__meta">
                <Box className="edit-order-dialog__meta-chips">
                    {metaChips.map((chip) => (
                        <span key={chip.label} className="edit-order-dialog__chip">
                            <strong>{chip.label}:</strong>
                            <span>{chip.value}</span>
                        </span>
                    ))}
                </Box>
            </Box>

            {isSavingBill && <LinearProgress color="secondary" />}

            <Tabs
                className="edit-order-dialog__mobile-tabs"
                value={mobilePanel}
                onChange={(_e, val) => setMobilePanel(val)}
                variant="fullWidth"
                indicatorColor="secondary"
                textColor="secondary"
            >
                <Tab
                    icon={<RestaurantMenuIcon fontSize="small" />}
                    iconPosition="start"
                    label="Menu"
                />
                <Tab
                    icon={
                        <Badge badgeContent={itemCount || null} color="secondary" max={99}>
                            <ReceiptLongIcon fontSize="small" />
                        </Badge>
                    }
                    iconPosition="start"
                    label="Bill"
                />
            </Tabs>

            <Box className="edit-order-dialog__layout">
                <Box
                    className={`edit-order-dialog__panel edit-order-dialog__panel--menu${
                        isMobile && mobilePanel !== 0 ? ' is-hidden-mobile' : ''
                    }`}
                >
                    <EditOrderMenuPanel editCart={editCart} onCartChange={setEditCart} />
                </Box>
                <Box
                    className={`edit-order-dialog__panel edit-order-dialog__panel--cart${
                        isMobile && mobilePanel !== 1 ? ' is-hidden-mobile' : ''
                    }`}
                >
                    {order && (
                        <EditOrderCart
                            editCart={editCart}
                            order={order}
                            onCartChange={setEditCart}
                        />
                    )}
                </Box>
            </Box>

            <Box className="edit-order-dialog__advanced">
                <Accordion defaultExpanded={false} disableGutters elevation={0}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2" fontWeight={700}>
                            Advanced options
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 1, display: { xs: 'none', sm: 'inline' } }}
                        >
                            — change bill date or delete order
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DemoContainer
                                components={[
                                    'DatePicker',
                                    'TimePicker',
                                    'DateTimePicker',
                                    'DateRangePicker',
                                ]}
                            >
                                <DemoItem label="Bill date (createdAt)">
                                    <DateTimePicker
                                        value={dayjs(formData || order?.createdAt)}
                                        onChange={(e) => handleDateChange(e)}
                                        slotProps={{
                                            textField: { size: 'small', fullWidth: true },
                                        }}
                                    />
                                </DemoItem>
                            </DemoContainer>
                        </LocalizationProvider>
                        <Box className="edit-order-dialog__advanced-actions">
                            {canUpdateOrder && (
                                <Button
                                    onClick={handleUpdateOrderDate}
                                    variant="outlined"
                                    color="primary"
                                >
                                    Update Date Only
                                </Button>
                            )}
                            {canDeleteOrder && (
                                <Button
                                    onClick={handleDeleteOrder}
                                    variant="outlined"
                                    color="error"
                                >
                                    Delete Order
                                </Button>
                            )}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            </Box>

            {isMobile && canUpdateOrder && (
                <Box className="edit-order-dialog__mobile-footer">
                    <Box className="edit-order-dialog__mobile-total">
                        <span className="label">Total</span>
                        <span className="amount">₹{billPreview?.totalCost ?? 0}</span>
                    </Box>
                    <Button
                        className="edit-order-dialog__save-btn"
                        variant="contained"
                        color="secondary"
                        disabled={isSavingBill}
                        onClick={handleSaveBill}
                        startIcon={<SaveIcon />}
                        sx={{ minWidth: 140, py: 1.2 }}
                    >
                        Save Bill
                    </Button>
                </Box>
            )}
        </Dialog>
    );
};

const mapStateToProps = (state: any) => {
    const { common, user } = state;
    return {
        selectedRestId: common?.selectedRestaurant?.docId || '',
        roles: user?.user?.roles || {},
        userPermissions: user?.user?.roles
            ? {
                  ...common?.globalConfig?.permissions,
                  ...(user?.user?.roles[common?.selectedRestaurant?.docId]?.permissions),
              }
            : {},
    };
};

export default connect(mapStateToProps)(UpdateOrder);
